'use client';
import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import {
  ShieldCheck, Clock, CheckCircle, XCircle, AlertCircle, ChevronRight,
  User, Building2, FileText, Loader2, ArrowLeft,
} from 'lucide-react';
import {
  submitVerificationRequest,
  getMyVerificationRequest,
  VerificationRequest,
} from '@/lib/db/trust-authenticity';

const STATUS_CONFIG = {
  pending: { label: 'Pending Review', icon: Clock, color: '#F59E0B', bg: '#F59E0B15' },
  under_review: { label: 'Under Review', icon: AlertCircle, color: '#3B82F6', bg: '#3B82F615' },
  approved: { label: 'Approved', icon: CheckCircle, color: '#22C55E', bg: '#22C55E15' },
  rejected: { label: 'Rejected', icon: XCircle, color: '#EF4444', bg: '#EF444415' },
};

export default function VerificationRequestPage() {
  const { user } = useAuth();
  const router = useRouter();
  const [existing, setExisting] = useState<VerificationRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    fullLegalName: '',
    institutionalAffiliation: '',
    roleDescription: '',
    notes: '',
  });

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    getMyVerificationRequest().then((req) => {
      setExisting(req);
      setLoading(false);
    });
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullLegalName.trim() || !form.roleDescription.trim()) {
      setError('Full legal name and role description are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const result = await submitVerificationRequest(form);
    setSubmitting(false);
    if (result) {
      setSuccess(true);
      const req = await getMyVerificationRequest();
      setExisting(req);
    } else {
      setError('Failed to submit request. Please try again.');
    }
  };

  if (!user) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
          <div className="text-center">
            <ShieldCheck size={48} className="mx-auto mb-4" style={{ color: 'var(--muted-foreground)' }} />
            <p className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Sign in to request verification</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
          <Loader2 size={32} className="animate-spin" style={{ color: 'var(--primary)' }} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen pt-20 pb-16" style={{ backgroundColor: 'var(--background)' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {/* Back */}
          <button
            onClick={() => router.back()}
            className="flex items-center gap-2 text-sm mb-6 hover:opacity-80 transition-opacity"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <ArrowLeft size={15} />
            Back
          </button>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--primary)15' }}>
                <ShieldCheck size={20} style={{ color: 'var(--primary)' }} />
              </div>
              <div>
                <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Identity Verification</h1>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Request senior tier upgrade with verified credentials</p>
              </div>
            </div>
          </div>

          {/* Existing request status */}
          {existing && (
            <div
              className="rounded-xl border p-5 mb-8"
              style={{
                backgroundColor: STATUS_CONFIG[existing.status].bg,
                borderColor: STATUS_CONFIG[existing.status].color + '40',
              }}
            >
              <div className="flex items-start gap-3">
                {React.createElement(STATUS_CONFIG[existing.status].icon, {
                  size: 20,
                  style: { color: STATUS_CONFIG[existing.status].color, flexShrink: 0, marginTop: 2 },
                })}
                <div className="flex-1">
                  <p className="font-semibold text-sm" style={{ color: STATUS_CONFIG[existing.status].color }}>
                    {STATUS_CONFIG[existing.status].label}
                  </p>
                  <p className="text-sm mt-1" style={{ color: 'var(--foreground)' }}>
                    Your verification request submitted on{' '}
                    {new Date(existing.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {' '}is {existing.status === 'pending' ? 'awaiting review' : existing.status === 'under_review' ? 'being reviewed by our team' : existing.status === 'approved' ? 'approved — your tier has been upgraded' : 'not approved at this time'}.
                  </p>
                  {existing.reviewerNotes && (
                    <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--card)' }}>
                      <p className="text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Reviewer notes:</p>
                      <p className="text-sm" style={{ color: 'var(--foreground)' }}>{existing.reviewerNotes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Form — only show if no pending/approved request */}
          {(!existing || existing.status === 'rejected') && !success && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div
                className="rounded-xl border p-6"
                style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
              >
                <h2 className="text-sm font-semibold uppercase tracking-wide mb-5" style={{ color: 'var(--muted-foreground)' }}>
                  Your Details
                </h2>

                <div className="space-y-4">
                  {/* Full legal name */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                      Full Legal Name <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <div className="relative">
                      <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                      <input
                        type="text"
                        value={form.fullLegalName}
                        onChange={(e) => setForm((f) => ({ ...f, fullLegalName: e.target.value }))}
                        placeholder="As it appears on your ID"
                        className="w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm outline-none focus:ring-2"
                        style={{
                          backgroundColor: 'var(--background)',
                          borderColor: 'var(--border)',
                          color: 'var(--foreground)',
                        }}
                        required
                      />
                    </div>
                  </div>

                  {/* Institutional affiliation */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                      Institutional Affiliation
                    </label>
                    <div className="relative">
                      <Building2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                      <input
                        type="text"
                        value={form.institutionalAffiliation}
                        onChange={(e) => setForm((f) => ({ ...f, institutionalAffiliation: e.target.value }))}
                        placeholder="University, museum, cultural institution, etc."
                        className="w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm outline-none focus:ring-2"
                        style={{
                          backgroundColor: 'var(--background)',
                          borderColor: 'var(--border)',
                          color: 'var(--foreground)',
                        }}
                      />
                    </div>
                  </div>

                  {/* Role description */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                      Role & Expertise <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <textarea
                      value={form.roleDescription}
                      onChange={(e) => setForm((f) => ({ ...f, roleDescription: e.target.value }))}
                      placeholder="Describe your role, expertise, and why you are requesting senior tier access..."
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 resize-none"
                      style={{
                        backgroundColor: 'var(--background)',
                        borderColor: 'var(--border)',
                        color: 'var(--foreground)',
                      }}
                      required
                    />
                  </div>

                  {/* Additional notes */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                      Additional Notes
                    </label>
                    <textarea
                      value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      placeholder="Any other information you would like to share..."
                      rows={3}
                      className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none focus:ring-2 resize-none"
                      style={{
                        backgroundColor: 'var(--background)',
                        borderColor: 'var(--border)',
                        color: 'var(--foreground)',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Info box */}
              <div
                className="rounded-xl border p-4 flex gap-3"
                style={{ backgroundColor: '#3B82F615', borderColor: '#3B82F640' }}
              >
                <FileText size={16} style={{ color: '#3B82F6', flexShrink: 0, marginTop: 2 }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: '#3B82F6' }}>Document verification</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                    After submitting, our team may contact you via email to request supporting documents (ID, credentials, or institutional letter). Verification typically takes 3–5 business days.
                  </p>
                </div>
              </div>

              {error && (
                <p className="text-sm text-center" style={{ color: '#EF4444' }}>{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
                {submitting ? 'Submitting…' : 'Submit Verification Request'}
              </button>
            </form>
          )}

          {success && (
            <div
              className="rounded-xl border p-8 text-center"
              style={{ backgroundColor: '#22C55E15', borderColor: '#22C55E40' }}
            >
              <CheckCircle size={40} className="mx-auto mb-3" style={{ color: '#22C55E' }} />
              <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--foreground)' }}>Request Submitted</h3>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Your verification request has been received. Our team will review it and get back to you within 3–5 business days.
              </p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
